import { createPortal } from 'react-dom';
import { Crown, Check, X, Gift, Share2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { track } from '../../analytics';
import { shareText } from '../../utils/nativeShare';
import { DOWNLOAD_URL } from '../../constants/links';

interface PremiumUpsellSheetProps {
  open: boolean;
  onClose: () => void;
}

const FEATURES = [
  'Full overlap details for all future months',
  'Unlimited roster imports',
  '3D flight map & route globe',
  'Advanced stats (airports, countries, miles)',
  'Achievements & badges',
  'Historical flight data',
  'Overlap push notifications',
];

export function PremiumUpsellSheet({ open, onClose }: PremiumUpsellSheetProps) {
  const { currentUser } = useApp();

  if (!open) return null;
  track('premium_upsell_shown');

  const referralLink = `https://rostandfound.app/?ref=${currentUser.handle}`;

  async function handleShare() {
    const text = `Join me on Rost & Found! Use my code "${currentUser.handle}" when you sign up and we both get 1 month of Premium free.\n\nDownload: ${DOWNLOAD_URL}\n${referralLink}`;
    const result = await shareText({ title: 'Rost & Found', text });
    if (result !== 'cancelled') {
      track('referral_shared', { method: result });
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-slate-800 rounded-t-2xl border-t border-slate-700 animate-slide-up"
        style={{ paddingBottom: 'calc(var(--sab, 0px) + 16px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-slate-600" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
        >
          <X size={18} />
        </button>

        <div className="px-5 pb-4">
          {/* Header */}
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Crown size={20} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-100">Upgrade to Premium</h3>
              <p className="text-xs text-slate-500">Unlock the full crew experience</p>
            </div>
          </div>

          {/* Feature list */}
          <div className="space-y-2.5 mb-5">
            {FEATURES.map((feature) => (
              <div key={feature} className="flex items-start gap-2.5">
                <Check size={14} className="text-amber-400 mt-0.5 shrink-0" />
                <span className="text-sm text-slate-300">{feature}</span>
              </div>
            ))}
          </div>

          {/* How to get premium */}
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Gift size={16} className="text-amber-400" />
              <span className="text-sm font-semibold text-amber-300">Get Premium Free</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Invite a friend to Rost & Found — when they sign up with your code, you both get 1 month of Premium.
            </p>
          </div>

          {/* Share CTA */}
          <button
            onClick={handleShare}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
          >
            <Share2 size={16} />
            Share Invite Link
          </button>

          <p className="text-center text-[11px] text-slate-600 mt-2">
            Your code: <span className="font-mono text-slate-500">@{currentUser.handle}</span>
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
